import type { BillingUsage } from "./api.js";

/**
 * Pure helpers for the b2c usage surfaces (header chip, billing page). Kept
 * framework-free and side-effect-free so they can be unit-tested directly.
 */

/** USD per million billable tokens: the credit rate metering converts each
 *  turn's real cost at (CREDIT_USD_PER_MTOK in the server's billing/plans.ts,
 *  which the billing catalogue also serves). */
export const CREDIT_USD_PER_MTOK = 50;

/** Billable tokens for a turn's cost, as the server meters them. */
export function billableTokensForUsd(usd: number): number {
  return Math.round((usd / CREDIT_USD_PER_MTOK) * 1_000_000);
}

/** Compact token count: 0–999 as-is, then 1.2k / 3.4M (one decimal only
 *  while the leading figure is small, so "1.2k" but "12k", "1.2M" but "12M"). */
export function compactTokens(n: number): string {
  if (n < 1000) return String(n);
  // Drop the decimal just before it would round up into the next magnitude
  // (e.g. 9999 → "10k", not "10.0k").
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 9950 ? 1 : 0)}k`;
  return `${(n / 1_000_000).toFixed(n < 9_950_000 ? 1 : 0)}M`;
}

/**
 * The boundary the usage bar measures against: the monthly token allowance.
 * Prepaid credits extend usage past this but are shown separately (they're a
 * top-up, not part of the included allowance). 0 = no allowance configured.
 */
export function usageLimit(usage: Pick<BillingUsage, "allowance">): number {
  return usage.allowance ?? 0;
}

/**
 * Rough document-count estimate from a token figure — DISPLAY ONLY, always
 * rendered as "≈" alongside the typical-document assumption. Mirrors the
 * server's docsFromTokens so the chip and pages agree.
 */
export function docsFromTokens(
  tokens: number,
  typicalDocTokens: number | undefined,
): number {
  const per = typicalDocTokens && typicalDocTokens > 0 ? typicalDocTokens : 90_000;
  return Math.max(0, Math.round(tokens / per));
}

/** Percent of the limit consumed, clamped to 0–100. 0 when no limit. */
export function usagePct(used: number, limit: number): number {
  if (limit <= 0) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
}

/**
 * Rough "how long will the allowance last" projection from the run-rate so
 * far this period: remaining ÷ (used per day elapsed). Returns null when it
 * isn't meaningful — no limit, already over, nothing used yet, less than a
 * few hours of history (rate too noisy), or a result so far out (>90d) it's
 * not worth showing. `now` is injectable for testing.
 */
export function projectDaysLeft(
  usage: Pick<BillingUsage, "periodStartedAt">,
  used: number,
  limit: number,
  over: boolean,
  now: number = Date.now(),
): number | null {
  if (limit <= 0 || over || used <= 0 || !usage.periodStartedAt) return null;
  const elapsedMs = now - new Date(usage.periodStartedAt).getTime();
  const elapsedDays = elapsedMs / 86_400_000;
  if (elapsedDays < 0.25) return null;
  const perDay = used / elapsedDays;
  if (perDay <= 0) return null;
  const days = Math.floor((limit - used) / perDay);
  if (days < 1 || days > 90) return null;
  return days;
}
